import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Menu,
  X,
  MapPin,
  Building2,
  LayoutDashboard,
  FileText,
  Settings,
  LogOut,
  ChevronDown,
  User,
  PencilLine,
  Facebook,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';


const LOGO_URL = 'https://cdn.cdmx.gob.mx/identidad/cdmx/img/logo-cdmx-vertical.png';

const isRouteActive = (pathname, targetPath) => {
  if (targetPath === '/') return pathname === '/';
  return pathname === targetPath || pathname.startsWith(`${targetPath}/`);
};

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout, isAdmin, canEdit } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const mobileMenuRef = useRef(null);

  const navLinks = useMemo(
    () => [
      { path: '/', label: 'Inicio', icon: LayoutDashboard },
      { path: '/territorios', label: 'Territorios', icon: MapPin },
      { path: '/dependencias', label: 'Dependencias', icon: Building2 },
      { path: '/resumen', label: 'Resumen Semanal', icon: FileText },
    ],
    []
  );

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setMobileMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [mobileMenuOpen]);

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[#D4C5A5]/50 shadow-sm">
      <div ref={mobileMenuRef} className="relative max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20 gap-3">
          <Link to="/" className="min-w-0 flex items-center gap-2 sm:gap-3 group">
            <img
              src={LOGO_URL}
              alt="Ciudad de México"
              className="h-8 sm:h-10 lg:h-14 w-auto transition-transform group-hover:scale-105 shrink-0"
            />

            <div className="min-w-0">
              <h1 className="text-xs sm:text-sm lg:text-base font-bold text-[#8B1C4A] leading-tight truncate">
                Territorios de Paz
              </h1>
              <p className="text-[10px] sm:text-xs text-[#BC955C] font-medium leading-tight truncate">
                e Igualdad
              </p>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const Icon = link.icon;
              const isActive = isRouteActive(location.pathname, link.path);

              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-all duration-200 ${
                    isActive
                      ? 'bg-[#8B1C4A] text-white'
                      : 'text-slate-700 hover:bg-[#F5F0E1] hover:text-[#8B1C4A]'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {link.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 hover:bg-[#F5F0E1] px-2 sm:px-3">
                    <div className="w-8 h-8 rounded-full bg-[#8B1C4A] flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <span className="hidden sm:block text-sm font-medium text-slate-700 max-w-[120px] truncate">
                      {user.name}
                    </span>
                    <ChevronDown className="hidden sm:block w-4 h-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end" className="w-64">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-slate-500">{user.email}</p>
                    <span
                      className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
                        isAdmin ? 'bg-[#8B1C4A] text-white' : 'bg-[#BC955C] text-black'
                      }`}
                    >
                      {isAdmin ? 'Super Admin' : user.role === 'editor' ? 'Editor' : 'Usuario'}
                    </span>
                  </div>

                  <DropdownMenuSeparator />

                  {canEdit && (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/cargar-actividad" className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Registrar actividad
                        </Link>
                      </DropdownMenuItem>

                      <DropdownMenuItem asChild>
                        <Link to="/mis-actividades" className="flex items-center gap-2">
                          <PencilLine className="w-4 h-4" />
                          Ver mis actividades
                        </Link>
                      </DropdownMenuItem>

                      {isAdmin && (
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="flex items-center gap-2">
                            <Settings className="w-4 h-4" />
                            Administración
                          </Link>
                        </DropdownMenuItem>
                      )}

                      <DropdownMenuSeparator />
                    </>
                  )}

                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login">
                <Button className="bg-[#8B1C4A] hover:bg-[#70163A] text-white h-10 px-3 sm:px-4 text-sm">
                  <span className="hidden sm:inline">Iniciar sesión</span>
                  <span className="sm:hidden">Ingresar</span>
                </Button>
              </Link>
            )}

            <button
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              className="lg:hidden p-2 rounded-md hover:bg-[#F5F0E1] transition-colors"
              aria-label="Abrir menú"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
  <>
    <div className="lg:hidden fixed inset-0 top-16 bg-black/20 z-40" />
    <nav className="lg:hidden absolute left-0 right-0 top-full z-50 bg-white py-3 border-t border-[#D4C5A5]/30 shadow-lg rounded-b-xl">
      <div className="px-3 space-y-1">
        {navLinks.map((link) => {
          const Icon = link.icon;
          const isActive = isRouteActive(location.pathname, link.path);

          return (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-md font-medium transition-colors ${
                isActive
                  ? 'bg-[#8B1C4A] text-white'
                  : 'text-slate-700 hover:bg-[#F5F0E1]'
              }`}
            >
              <Icon className="w-5 h-5" />
              {link.label}
            </Link>
          );
        })}
      </div>
    </nav>
  </>
)}
      </div>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="border-t-[6px] border-[#C29B5F] bg-[#FAF7EE]">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-6 py-6 text-center sm:px-8 md:grid-cols-2 md:text-left lg:grid-cols-[0.9fr_1fr_1fr_1.2fr_1fr] lg:items-start">
        <div className="flex justify-center lg:justify-start">
          <img
            src="https://cdn.cdmx.gob.mx/identidad/cdmx/img/logo-cdmx-vertical.png"
            alt="Ciudad de México"
            className="h-16 w-auto object-contain sm:h-20"
          />
        </div>

        <div className="text-[#555555]">
          <p className="text-sm leading-snug sm:text-base">
            Para emergencias,
            <br className="hidden sm:block" />
            <span className="sm:hidden"> </span>
            marca al{' '}
            <a
              href="tel:911"
              className="font-semibold text-[#991B1B] underline underline-offset-2 hover:text-[#7C3AED]"
            >
              911
            </a>
          </p>

          <p className="mt-3 text-sm leading-snug md:mt-4 sm:text-base">
            Dudas e información,
            <br className="hidden sm:block" />
            <span className="sm:hidden"> </span>
            marca al{' '}
            <a
              href="tel:*0311"
              className="font-semibold text-[#991B1B] underline underline-offset-2 hover:text-[#7C3AED]"
            >
              *0311
            </a>
          </p>
        </div>

        <div>
  <h3 className="text-sm text-center font-black text-[#991B1B] sm:text-base">
    Redes Sociales de la CDMX
  </h3>

  <div className="mt-3 flex items-center justify-center gap-5 text-[#C29B5F]">
    <a
      href="https://www.facebook.com/GobiernoCDMX"
      target="_blank"
      rel="noreferrer"
      aria-label="Facebook Gobierno CDMX"
      className="transition hover:text-[#991B1B]"
    >
      <Facebook className="h-7 w-7 fill-current md:h-8 md:w-8" />
    </a>

    <a
      href="https://x.com/GobCDMX"
      target="_blank"
      rel="noreferrer"
      aria-label="X Gobierno CDMX"
      className="text-3xl font-light leading-none transition hover:text-[#991B1B] sm:text-4xl"
    >
      𝕏
    </a>
  </div>
</div>

        <div>
          <h3 className="text-sm font-black text-[#991B1B] sm:text-base">
            Sitios relacionados
          </h3>

          <a
            href="https://adip.cdmx.gob.mx/"
            target="_blank"
            rel="noreferrer"
            className="mt-2 block text-sm font-semibold text-[#1E65B7] underline-offset-2 hover:text-[#991B1B] hover:underline sm:text-base"
          >
            Agencia Digital de Innovación Pública
          </a>

          <h4 className="mt-4 text-base font-black text-[#991B1B] md:text-base">
            Transparencia
          </h4>

          <a
            href="https://www.transparencia.cdmx.gob.mx/"
            target="_blank"
            rel="noreferrer"
            className="mt-2 block text-sm font-semibold text-[#1E65B7] underline-offset-2 hover:text-[#991B1B] hover:underline sm:text-base"
          >
            Ir al portal de Transparencia
          </a>
        </div>

        <div className="flex justify-center md:justify-start lg:justify-end">
          <a
            href="https://datos.cdmx.gob.mx/"
            target="_blank"
            rel="noreferrer"
            aria-label="Portal de Datos Abiertos CDMX"
            className="block transition hover:opacity-85"
          >
            <img
              src="https://frontendbase.cdmx.gob.mx/file-server/banners/346_banner.png?v=1778109867005"
              alt="Portal de Datos Abiertos"
              className="h-24 w-auto rounded-xl object-contain md:h-28"
            />
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Header;